import React, {ChangeEvent, useState, useEffect, useRef} from 'react';
import b_ from 'b_';
import plural from 'plural-ru';
import pick from 'lodash/pick';
import {Upload} from 'tus-js-client';

import {getSortedTranslations} from 'assets/i18n';
import I18N from 'assets/i18n/brandlift-project';

import history from 'client/common/utils/history';
import {Textinput, Button, Icon, Spin} from 'client/common/components/lego';
import ClientSelect from 'client/common/components/client-select';
import VideoUpload from 'client/common/components/video-upload';
import {getVideoInfo, parseVideoLink, VideoErrorReason} from 'client/common/utils/video';
import {formatHHMMSS} from 'client/common/utils/format';
import cfg from 'client/common/utils/client-config';
import {useUser} from 'client/common/contexts/user';

import {CreativeCreateData} from 'client/creatives/types';

import './creative-loader-popup.css';

const i18n = I18N.creatives.creativeLoader;
const b = b_.with('creative-loader-popup');

function getCreateLabel(count: number): string {
    return plural(count, ...(getSortedTranslations(i18n.createLabel) as [string, string, string]));
}

interface ComponentProps {
    multiple?: boolean;
    submitText?: string;
    onSubmit: (params: {creatives: CreativeCreateData[]; clientId?: string}) => Promise<void>;
}

type CreativeToAdd = Pick<CreativeCreateData, 'id' | 'service' | 'title'> &
    Partial<Pick<CreativeCreateData, 'duration'>> & {
        progress?: boolean;
        error?: string;
        upload?: Upload;
        durationTimeout?: any;
    };

function getReadyCreatives(creatives: CreativeToAdd[]) {
    return creatives
        .filter(({progress, error}) => {
            return !progress && !error;
        })
        .map(({id, service, title, duration}) => ({
            id,
            service,
            title,
            duration
        })) as CreativeCreateData[];
}

function getErrorMessageByReason(reason: VideoErrorReason) {
    switch (reason) {
        case 'DURATION_TOO_LONG':
            return i18n.error.DURATION_TOO_LONG.replace(
                '%s',
                String(cfg.creative.minDuration)
            ).replace('%s', String(cfg.creative.maxDuration));
        case 'SIZE_TOO_BIG':
            return i18n.error.SIZE_TOO_BIG.replace('%s', String(cfg.creative.maxSizeMb));
        default:
            return i18n.error[reason];
    }
}

// eslint-disable-next-line max-statements, complexity
export default function CreativeLoaderPopup(props: ComponentProps) {
    const {multiple = false, submitText, onSubmit} = props;

    const [url, setUrl] = useState('');
    const [urlError, setUrlError] = useState<string>();
    const [error, setError] = useState<string>();
    const [creatives, setCreatives] = useState<CreativeToAdd[]>([]);
    const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

    const [clientId, setClientId] = useState<string>();

    const [createPending, setCreatePending] = useState(false);

    const user = useUser();

    const readyCreatives = getReadyCreatives(creatives);
    const isUploading = creatives.some(({progress}) => progress);

    const latesetCreatives = useRef(creatives);

    useEffect(() => {
        if (!creatives.length) {
            return;
        }

        const confirmLeave = (e: BeforeUnloadEvent) => {
            e.preventDefault();

            return (e.returnValue = i18n.beforeLeavePage);
        };

        // Ловим переход по урлу/перезагрузку страницы
        window.addEventListener('beforeunload', confirmLeave);

        // Ловим history.push
        const unblock = history.block(i18n.beforeLeavePage);

        return () => {
            window.removeEventListener('beforeunload', confirmLeave);
            unblock();
        };
    }, [creatives]);

    useEffect(() => {
        latesetCreatives.current = creatives;
    });

    // Останавливаем все незавершенные загрузки при анмаунте
    useEffect(() => {
        return () => {
            async function stopUploadings() {
                for (const creative of latesetCreatives.current) {
                    if (creative.upload) {
                        await creative.upload.abort();
                    }
                    if (creative.durationTimeout) {
                        clearTimeout(creative.durationTimeout);
                    }
                }
            }

            void stopUploadings();
        };
    }, []);

    function handleUrlChange(event: ChangeEvent<HTMLInputElement>) {
        setUrl(event.target.value);
        setUrlError(undefined);
    }

    async function handleAdd() {
        setError(undefined);

        const video = parseVideoLink(url);

        const id = video?.id;
        const service = video?.service;

        if (!service || !id) {
            return setUrlError(i18n.invalidUrl);
        }

        const info = await getVideoInfo(service, id);

        if ('error' in info) {
            const reason = info.error?.reason ?? 'UPLOAD_ERROR';

            return setUrlError(getErrorMessageByReason(reason));
        }

        setCreatives([
            ...creatives,
            pick(info, ['service', 'id', 'title', 'duration']) as CreativeCreateData
        ]);

        setUrl('');
    }

    async function handleRemove(index: number) {
        const creative = creatives[index];

        if (creative.upload) {
            await creative.upload.abort();
        }

        if (creative.durationTimeout) {
            clearTimeout(creative.durationTimeout);
        }

        setCreatives(old => old.filter((_, idx) => idx !== index));
    }

    async function handleCreateCreatives() {
        setCreatePending(true);

        await onSubmit({creatives: readyCreatives, clientId});

        setCreatives([]);
        setCreatePending(false);
    }

    const handleDurationUpdated = (tempCreativeId: string, timeout: number) => {
        setCreatives(old => {
            return old.map(creative => {
                if (creative.id === tempCreativeId) {
                    return {
                        ...creative,
                        durationTimeout: timeout
                    };
                }

                return creative;
            });
        });
    };

    function handleErrorTooLong(creativeId: string, duration: number, fileName?: File['name']) {
        const errorMessage = getErrorMessageByReason('DURATION_TOO_LONG');

        // определили duration по содержимому файла, добавляем новую строку с файлом
        if (fileName) {
            return setCreatives(old => [
                ...old,
                {
                    title: fileName,
                    service: 'resource',
                    id: creativeId,
                    duration,
                    progress: false,
                    error: errorMessage
                }
            ]);
        }

        // обновление duration в существующей строке с файлом
        setCreatives(old => {
            return old.map(creative => {
                if (creative.id === creativeId) {
                    return {
                        ...creative,
                        duration,
                        progress: false,
                        error: errorMessage
                    };
                }

                return creative;
            });
        });
    }

    const handleErrorTooBig = (creativeId: string, fileName: File['name']) => {
        setCreatives(old => [
            ...old,
            {
                title: fileName,
                service: 'resource',
                id: creativeId,
                error: getErrorMessageByReason('SIZE_TOO_BIG')
            }
        ]);
    };

    const handleUploadStart = (creativeId: string, fileName: File['name']) => {
        setCreatives(old => [
            ...old,
            {
                title: fileName,
                service: 'resource',
                id: creativeId,
                progress: true
            }
        ]);

        setIsShowWarning(true);
        setError(undefined);
    };

    const handleUploadError = (creativeId: string) => {
        setCreatives(old => {
            return old.map(creative => {
                if (creative.id === creativeId) {
                    return {
                        ...creative,
                        progress: false,
                        error: getErrorMessageByReason('UPLOAD_ERROR')
                    };
                }

                return creative;
            });
        });
    };

    const handleError = (code: string, file?: {id: string; name?: string; duration?: number}) => {
        setError(undefined);

        switch (code) {
            case 'file-too-large':
                if (file) {
                    handleErrorTooBig(file.id, file.name!);
                }
                break;
            case 'duration-too-long':
                if (file) {
                    handleErrorTooLong(file.id, file.duration!, file.name);
                }
                break;
            case 'upload-error':
                if (file) {
                    handleUploadError(file.id);
                }
                break;
            case 'too-many-files':
                setError(i18n.error.TOO_MANY_FILES);
                break;
            default:
                console.error(code);
                break;
        }

        setIsShowWarning(false);
    };

    const handleUploadSuccess = (
        tempCreativeId: string,
        duration: number,
        data: {resourceId: string}
    ) => {
        setCreatives(old => {
            return old.map(creative => {
                if (creative.id === tempCreativeId) {
                    return {
                        ...creative,
                        duration,
                        id: data.resourceId,
                        progress: false
                    };
                }

                return creative;
            });
        });

        setIsShowWarning(false);
    };

    const handleSaveUploadInstance = (temporaryCreativeId: string, uploadInstance?: Upload) => {
        setCreatives(old => {
            return old.map(creative => {
                if (creative.id === temporaryCreativeId) {
                    return {
                        ...creative,
                        upload: uploadInstance
                    };
                }

                return creative;
            });
        });
    };

    function handleUploadProgress(bytesUploaded: number, bytesTotal: number) {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);

        console.log(bytesUploaded, bytesTotal, `${percentage}%`);
    }

    let canUpload = multiple ? true : creatives.length === 0;

    if (user?.info?.authInfo?.isAgency) {
        canUpload = canUpload && Boolean(clientId);
    }

    return (
        <div className={b()}>
            {user?.info?.authInfo?.isAgency && (
                <div className={b('input-group')}>
                    <ClientSelect
                        className={b('agency-client-select')}
                        selectedId={clientId}
                        onChange={id => setClientId(id)}
                        showRemainPackages
                    />
                </div>
            )}
            {canUpload && (
                <>
                    <div className={b('input-group')}>
                        <Textinput
                            placeholder={i18n.inputPlaceholder}
                            className={b('input')}
                            hint={urlError}
                            state={urlError ? 'error' : undefined}
                            size='m'
                            view='default'
                            value={url}
                            onChange={handleUrlChange}
                        />
                        <Button
                            size='icon'
                            view='clear'
                            className={b('button', {type: 'add'})}
                            onClick={handleAdd}
                            icon={(className: string) => <Icon className={className} type='plus' />}
                        />
                    </div>
                    <VideoUpload
                        className={b('video-upload')}
                        clientId={clientId}
                        minDurationSec={cfg.creative.minDuration}
                        maxDurationSec={cfg.creative.maxDuration}
                        maxSizeMb={cfg.creative.maxSizeMb}
                        maxFiles={multiple ? undefined : 1}
                        onUploadInstanceReady={handleSaveUploadInstance}
                        onDurationUpdated={handleDurationUpdated}
                        onStart={handleUploadStart}
                        onProgress={handleUploadProgress}
                        onSuccess={handleUploadSuccess}
                        onError={e => handleError(e.code, e.file)}
                    />
                </>
            )}

            {creatives.length > 0 && (
                <div className={b('creatives')}>
                    {creatives.map((creative, index) => {
                        const {title, duration, error: creativeError} = creative;

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className={b('creative-wrapper')} key={`${index}`}>
                                <div
                                    className={b('creative', {error: creativeError ? 'yes' : 'no'})}
                                >
                                    {creative.progress ? (
                                        <Spin
                                            className={b('spinner')}
                                            size='xxs'
                                            view='default'
                                            progress
                                        />
                                    ) : null}
                                    {duration ? (
                                        <span className={b('creative-duration')}>
                                            {formatHHMMSS(duration)}
                                        </span>
                                    ) : null}
                                    <span className={b('creative-title')} title={title}>
                                        {title}
                                    </span>
                                    <Button
                                        size='icon'
                                        view='clear'
                                        className={b('button', {type: 'creative-remove'})}
                                        onClick={async () => handleRemove(index)}
                                        icon={(className: string) => (
                                            <Icon className={className} type='cross' size='xs' />
                                        )}
                                    />
                                </div>
                                {creativeError && (
                                    <span className={b('creative-error')}>{creativeError}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {isShowWarning && <p className={b('loading-warning')}>{i18n.warningMessage}</p>}
            {error && <p className={b('loading-error')}>{error}</p>}
            {readyCreatives.length > 0 && (
                <div className={b('controls')}>
                    <Button
                        className={b('button', {type: 'create'})}
                        size='m'
                        view='action'
                        onClick={handleCreateCreatives}
                        progress={createPending}
                        disabled={isUploading || (user?.info?.authInfo.isAgency && !clientId)}
                    >
                        {submitText || getCreateLabel(readyCreatives.length)}
                    </Button>
                </div>
            )}
        </div>
    );
}
